import { useEffect, useMemo, useState } from 'react';
import { useTasksStore } from '@/stores/tasksStore';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Activity,
  AlertCircle,
  BarChart3,
  BrainCircuit,
  Clock,
  Database,
  Download,
  LineChart,
  Network,
  RefreshCw,
  Sigma,
} from 'lucide-react';

type TimeRange = 'week' | 'month' | 'quarter' | 'year' | 'all';
type ChartMode = 'bar' | 'line' | 'scatter' | 'heatmap';

interface MetricCard {
  label: string;
  value: string;
  detail: string;
  icon: typeof Activity;
}

interface NumericSeries {
  key: string;
  label: string;
  values: Array<number | null>;
}

interface ActivitySample {
  id: string;
  title: string;
  status: string;
  bundle: string;
  project: string;
  assignee: string;
  priorityScore: number | null;
  durationMinutes: number | null;
  remainingMinutes: number | null;
  completionPercent: number | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  dueDate: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  requiredP: number | null;
  requiredRJ: number | null;
  returnP: number | null;
  returnRJ: number | null;
  resistance: number | null;
  focus: number | null;
  emotion: string;
  customAttributes: Record<string, unknown>;
}

interface AnalyticsBundle {
  activities: ActivitySample[];
  apiAnalytics: Record<string, any> | null;
  exportedTraining: Record<string, any> | null;
}

const EMPTY_BUNDLE: AnalyticsBundle = {
  activities: [],
  apiAnalytics: null,
  exportedTraining: null,
};

const rangeLabels: Record<TimeRange, string> = {
  week: 'This week',
  month: 'This month',
  quarter: 'This quarter',
  year: 'This year',
  all: 'All time',
};

export function Analytics() {
  const tasks = useTasksStore(s => s.tasks);
  const [timeRange, setTimeRange] = useState<TimeRange>('month');
  const [chartMode, setChartMode] = useState<ChartMode>('bar');
  const [xMetric, setXMetric] = useState('hour');
  const [yMetric, setYMetric] = useState('duration');
  const [analysisQuery, setAnalysisQuery] = useState('correlate duration completion');
  const [bundle, setBundle] = useState<AnalyticsBundle>(EMPTY_BUNDLE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    void loadAnalytics();
  }, [tasks]);

  async function loadAnalytics() {
    setLoading(true);
    setError('');
    try {
      const localActivities = normalizeActivities(tasks);
      const [analyticsResponse, exportResponse] = await Promise.all([
        api.training.getAnalytics(),
        api.training.exportTrainingData({ format: 'json' }),
      ]);
      setBundle({
        activities: localActivities,
        apiAnalytics: analyticsResponse.success ? analyticsResponse.data || null : null,
        exportedTraining: exportResponse.success ? exportResponse.data || null : null,
      });
      if (!analyticsResponse.success && !exportResponse.success) {
        setError(analyticsResponse.error?.message || exportResponse.error?.message || 'Backend analytics unavailable.');
      }
    } catch (cause) {
      setBundle({
        ...EMPTY_BUNDLE,
        activities: normalizeActivities(tasks),
      });
      setError(cause instanceof Error ? cause.message : 'Analytics could not be loaded.');
    } finally {
      setLoading(false);
    }
  }

  const filteredActivities = useMemo(
    () => filterByRange(bundle.activities, timeRange),
    [bundle.activities, timeRange]
  );

  const series = useMemo(
    () => buildNumericSeries(filteredActivities, bundle.exportedTraining),
    [filteredActivities, bundle.exportedTraining]
  );

  const metricOptions = series.map(item => ({ value: item.key, label: item.label }));
  const xSeries = series.find(item => item.key === xMetric) || series[0];
  const ySeries = series.find(item => item.key === yMetric) || series[1] || series[0];
  const correlations = useMemo(() => buildCorrelations(series), [series]);
  const commandResult = useMemo(
    () => runAnalysisQuery(analysisQuery, series, filteredActivities),
    [analysisQuery, series, filteredActivities]
  );
  const modelError = bundle.apiAnalytics?.modelError || {};
  const trainingShape = bundle.apiAnalytics?.trainingDataShape || {};

  const completed = filteredActivities.filter(activity => isCompleted(activity.status));
  const metricCards: MetricCard[] = [
    {
      label: 'Activities',
      value: String(filteredActivities.length),
      detail: `${completed.length} completed in ${rangeLabels[timeRange].toLowerCase()}`,
      icon: Activity,
    },
    {
      label: 'Completion',
      value: `${formatNumber(percent(completed.length, filteredActivities.length), 1)}%`,
      detail: 'Share of visible activities completed',
      icon: Sigma,
    },
    {
      label: 'Avg duration',
      value: `${formatNumber(average(filteredActivities.map(activity => activity.durationMinutes)), 0)}m`,
      detail: 'Estimated or actual duration where present',
      icon: Clock,
    },
    {
      label: 'Duration MAE',
      value: `${formatNumber(modelError.duration?.maeMinutes, 1)}m`,
      detail: `${modelError.duration?.sampleCount || 0} duration training samples`,
      icon: BrainCircuit,
    },
  ];

  return (
    <div className="min-h-full px-3 py-4 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-5">
        <section className="rounded-md border bg-card/90 p-5 shadow-sm backdrop-blur-xl">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <Badge variant="outline" className="mb-3 rounded-md">
                Analytics engine
              </Badge>
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Activity analytics</h1>
              <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
                Explore activity, energy, emotion, duration, custom field, and model-error data. The page
                is defensive by design: partial rows become unknown values instead of crashing the app.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={timeRange} onValueChange={value => setTimeRange(value as TimeRange)}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(rangeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={loadAnalytics} disabled={loading}>
                <RefreshCw className={loading ? 'mr-2 h-4 w-4 animate-spin' : 'mr-2 h-4 w-4'} />
                Refresh
              </Button>
            </div>
          </div>
          {error && (
            <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </section>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {metricCards.map(card => {
            const Icon = card.icon;
            return (
              <Card key={card.label} className="rounded-md">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
                  <CardTitle className="text-sm font-medium">{card.label}</CardTitle>
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="text-2xl font-semibold">{card.value}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{card.detail}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(360px,0.55fr)]">
          <Card className="rounded-md">
            <CardHeader className="p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <BarChart3 className="h-4 w-4" />
                    Graph workspace
                  </CardTitle>
                  <CardDescription>Switch chart type and compare any numeric metric.</CardDescription>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  <Select value={chartMode} onValueChange={value => setChartMode(value as ChartMode)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bar">Bar</SelectItem>
                      <SelectItem value="line">Line</SelectItem>
                      <SelectItem value="scatter">Scatter</SelectItem>
                      <SelectItem value="heatmap">Heatmap</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={xMetric} onValueChange={setXMetric}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {metricOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={yMetric} onValueChange={setYMetric}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {metricOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {filteredActivities.length === 0 ? (
                <EmptyAnalyticsState />
              ) : (
                <MetricChart mode={chartMode} xSeries={xSeries} ySeries={ySeries} />
              )}
            </CardContent>
          </Card>

          <Card className="rounded-md">
            <CardHeader className="p-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Network className="h-4 w-4" />
                Correlations
              </CardTitle>
              <CardDescription>Top Pearson correlations from visible metrics.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 p-4 pt-0">
              {correlations.length === 0 ? (
                <p className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
                  More numeric samples are needed before correlations are meaningful.
                </p>
              ) : (
                correlations.slice(0, 8).map(item => (
                  <div key={`${item.a}:${item.b}`} className="rounded-md border p-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate font-medium">
                        {item.aLabel} vs {item.bLabel}
                      </span>
                      <Badge variant={Math.abs(item.r) >= 0.65 ? 'default' : 'outline'} className="rounded-md">
                        r {formatNumber(item.r, 2)}
                      </Badge>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                      <div
                        className={item.r >= 0 ? 'h-full bg-primary' : 'h-full bg-destructive'}
                        style={{ width: `${Math.min(100, Math.abs(item.r) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px]">
          <Card className="rounded-md">
            <CardHeader className="p-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <LineChart className="h-4 w-4" />
                Model error and training shape
              </CardTitle>
              <CardDescription>Prediction error by sample class, week, and collected dataset.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 p-4 pt-0">
              <div className="grid gap-3 sm:grid-cols-3">
                <StatPill label="Duration samples" value={trainingShape.durationSamples || modelError.duration?.sampleCount || 0} />
                <StatPill label="Feedback rows" value={trainingShape.feedbackSamples || 0} />
                <StatPill label="Custom fields" value={trainingShape.customFields || 0} />
              </div>
              <div className="grid gap-4 xl:grid-cols-2">
                <ErrorList
                  title="Error by activity class"
                  rows={modelError.duration?.byClass || []}
                  empty="No class-level duration errors yet."
                />
                <ErrorList
                  title="Weekly error trend"
                  rows={modelError.duration?.trend || []}
                  empty="No duration trend yet."
                />
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-md">
            <CardHeader className="p-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Sigma className="h-4 w-4" />
                Analysis console
              </CardTitle>
              <CardDescription>Small analysis grammar for quick queries.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 p-4 pt-0">
              <Label htmlFor="analysisQuery">Command</Label>
              <Textarea
                id="analysisQuery"
                value={analysisQuery}
                onChange={event => setAnalysisQuery(event.target.value)}
                className="min-h-24 font-mono text-xs"
                placeholder="correlate duration completion"
              />
              <div className="rounded-md border bg-muted/30 p-3 font-mono text-xs leading-6">
                {commandResult}
              </div>
              <div className="flex flex-wrap gap-2">
                {['correlate duration completion', 'average duration by bundle', 'average p_return by emotion'].map(command => (
                  <Button key={command} type="button" variant="outline" size="sm" onClick={() => setAnalysisQuery(command)}>
                    {command}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="rounded-md">
          <CardHeader className="p-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="h-4 w-4" />
              Training data export
            </CardTitle>
            <CardDescription>
              Export raw and derived training data for external modeling: specificities, custom fields,
              predictions, actuals, deltas, sessions, and activity metadata.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 p-4 pt-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-3">
              <span>{trainingShape.tasks || filteredActivities.length} activities</span>
              <span>{trainingShape.sessions || 0} sessions</span>
              <span>{trainingShape.energySamples || 0} energy samples</span>
            </div>
            <Button variant="outline" onClick={() => downloadJson(bundle.exportedTraining || {})}>
              <Download className="mr-2 h-4 w-4" />
              Export JSON
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EmptyAnalyticsState() {
  return (
    <div className="flex min-h-[320px] flex-col items-center justify-center rounded-md border border-dashed bg-muted/25 p-6 text-center">
      <AlertCircle className="mb-3 h-10 w-10 text-muted-foreground" />
      <h3 className="text-base font-medium">No analytics samples in this range</h3>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        Complete or log sessions, then this view will populate graphs, correlations, and prediction error.
      </p>
    </div>
  );
}

function MetricChart({
  mode,
  xSeries,
  ySeries,
}: {
  mode: ChartMode;
  xSeries?: NumericSeries;
  ySeries?: NumericSeries;
}) {
  const points = zipSeries(xSeries, ySeries);
  if (!xSeries || !ySeries || points.length === 0) return <EmptyAnalyticsState />;
  if (mode === 'heatmap') return <Heatmap points={points} xLabel={xSeries.label} yLabel={ySeries.label} />;
  if (mode === 'scatter') return <ScatterPlot points={points} xLabel={xSeries.label} yLabel={ySeries.label} />;
  const grouped = groupByRoundedX(points);
  return <SeriesSvg mode={mode} rows={grouped} xLabel={xSeries.label} yLabel={ySeries.label} />;
}

function SeriesSvg({
  mode,
  rows,
  xLabel,
  yLabel,
}: {
  mode: 'bar' | 'line';
  rows: Array<{ x: number; y: number; count: number }>;
  xLabel: string;
  yLabel: string;
}) {
  const width = 840;
  const height = 320;
  const padding = 42;
  const maxY = Math.max(1, ...rows.map(row => row.y));
  const minX = Math.min(...rows.map(row => row.x));
  const maxX = Math.max(...rows.map(row => row.x));
  const xScale = (value: number) =>
    padding + ((value - minX) / Math.max(1, maxX - minX)) * (width - padding * 2);
  const yScale = (value: number) => height - padding - (value / maxY) * (height - padding * 2);
  const line = rows.map(row => `${xScale(row.x)},${yScale(row.y)}`).join(' ');

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="min-h-[320px] min-w-[720px] rounded-md border bg-background">
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="hsl(var(--border))" />
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="hsl(var(--border))" />
        <text x={padding} y={24} fill="hsl(var(--muted-foreground))" fontSize="12">
          {yLabel}
        </text>
        <text x={width - padding} y={height - 10} fill="hsl(var(--muted-foreground))" fontSize="12" textAnchor="end">
          {xLabel}
        </text>
        {mode === 'bar'
          ? rows.map((row, index) => {
              const x = xScale(row.x);
              const barWidth = Math.max(10, (width - padding * 2) / Math.max(1, rows.length) - 8);
              const y = yScale(row.y);
              return (
                <g key={`${row.x}:${index}`}>
                  <rect
                    x={x - barWidth / 2}
                    y={y}
                    width={barWidth}
                    height={height - padding - y}
                    rx={4}
                    fill="hsl(var(--primary))"
                    opacity={0.82}
                  />
                  <text x={x} y={height - 24} textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="10">
                    {row.x}
                  </text>
                </g>
              );
            })
          : (
            <>
              <polyline points={line} fill="none" stroke="hsl(var(--primary))" strokeWidth="3" strokeLinejoin="round" />
              {rows.map((row, index) => (
                <circle key={`${row.x}:${index}`} cx={xScale(row.x)} cy={yScale(row.y)} r={4} fill="hsl(var(--primary))" />
              ))}
            </>
          )}
      </svg>
    </div>
  );
}

function ScatterPlot({
  points,
  xLabel,
  yLabel,
}: {
  points: Array<{ x: number; y: number }>;
  xLabel: string;
  yLabel: string;
}) {
  const width = 840;
  const height = 320;
  const padding = 42;
  const xs = points.map(point => point.x);
  const ys = points.map(point => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const xScale = (value: number) =>
    padding + ((value - minX) / Math.max(1, maxX - minX)) * (width - padding * 2);
  const yScale = (value: number) =>
    height - padding - ((value - minY) / Math.max(1, maxY - minY)) * (height - padding * 2);

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="min-h-[320px] min-w-[720px] rounded-md border bg-background">
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="hsl(var(--border))" />
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="hsl(var(--border))" />
        <text x={padding} y={24} fill="hsl(var(--muted-foreground))" fontSize="12">
          {yLabel}
        </text>
        <text x={width - padding} y={height - 10} fill="hsl(var(--muted-foreground))" fontSize="12" textAnchor="end">
          {xLabel}
        </text>
        {points.map((point, index) => (
          <circle
            key={`${point.x}:${point.y}:${index}`}
            cx={xScale(point.x)}
            cy={yScale(point.y)}
            r={4}
            fill="hsl(var(--primary))"
            opacity={0.7}
          />
        ))}
      </svg>
    </div>
  );
}

function Heatmap({
  points,
  xLabel,
  yLabel,
}: {
  points: Array<{ x: number; y: number }>;
  xLabel: string;
  yLabel: string;
}) {
  const buckets = new Map<string, number>();
  for (const point of points) {
    const key = `${Math.round(point.x)}:${Math.round(point.y)}`;
    buckets.set(key, (buckets.get(key) || 0) + 1);
  }
  const rows = [...buckets.entries()].map(([key, count]) => {
    const [x, y] = key.split(':').map(Number);
    return { x, y, count };
  });
  const maxCount = Math.max(1, ...rows.map(row => row.count));
  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>{xLabel}</span>
        <span>{yLabel}</span>
      </div>
      <div className="grid max-h-[320px] grid-cols-2 gap-2 overflow-auto rounded-md border bg-background p-3 sm:grid-cols-3 lg:grid-cols-4">
        {rows.map(row => (
          <div key={`${row.x}:${row.y}`} className="rounded-md border p-3 text-xs">
            <div
              className="mb-2 h-2 rounded-full bg-primary"
              style={{ opacity: 0.2 + 0.8 * (row.count / maxCount) }}
            />
            <div className="font-medium">
              {row.x} {'->'} {row.y}
            </div>
            <div className="text-muted-foreground">{row.count} sample{row.count === 1 ? '' : 's'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatPill({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="rounded-md border bg-muted/25 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold">{String(value ?? 0)}</div>
    </div>
  );
}

function ErrorList({
  title,
  rows,
  empty,
}: {
  title: string;
  rows: any[];
  empty: string;
}) {
  return (
    <div className="rounded-md border">
      <div className="border-b px-3 py-2 text-sm font-medium">{title}</div>
      {rows.length === 0 ? (
        <p className="p-3 text-sm text-muted-foreground">{empty}</p>
      ) : (
        <div className="divide-y">
          {rows.slice(0, 8).map((row, index) => (
            <div key={`${row.activity_class || row.bucket || index}`} className="grid grid-cols-[1fr_auto] gap-3 p-3 text-sm">
              <div className="min-w-0 truncate">{row.activity_class || row.bucket || 'Unknown'}</div>
              <div className="text-right text-muted-foreground">
                {formatNumber(row.mae_minutes ?? row.maeMinutes, 1)}m
                <span className="ml-2 text-xs">n={row.samples ?? row.sampleCount ?? 0}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function normalizeActivities(rawTasks: unknown[]): ActivitySample[] {
  if (!Array.isArray(rawTasks)) return [];
  return rawTasks
    .map((raw, index) => {
      const task = raw && typeof raw === 'object' ? (raw as Record<string, any>) : {};
      const core = safeRecord(task.core_fields);
      const state = safeRecord(task.state_profile);
      const custom = safeRecord(task.custom_attributes);
      const ai = safeRecord(task.ai_metadata);
      return {
        id: String(task.id || core.id || `activity-${index}`),
        title: String(core.title || task.title || 'Untitled activity'),
        status: String(core.status || task.status || 'todo'),
        bundle: String(task.bundle_name || custom.bundle_name || task.bundle_id || 'Unbundled'),
        project: String(task.project_name || custom.project_name || task.project_id || 'No project'),
        assignee: String(custom.assigned_to_email || custom.assigned_to_user_id || task.assigned_to_user_id || task.user_id || 'Me'),
        priorityScore: priorityToNumber(core.priority ?? task.priority ?? task.priority_score),
        durationMinutes: numberOrNull(core.duration_minutes ?? task.duration_minutes ?? task.estimatedDuration ?? task.estimated_duration),
        remainingMinutes: numberOrNull(core.remaining_duration_minutes ?? task.remaining_duration ?? task.remainingDuration),
        completionPercent: numberOrNull(core.completion_percent ?? task.completion_percent ?? task.completionPercent),
        scheduledStart: stringOrNull(core.do_date ?? task.scheduledStart ?? task.scheduled_start),
        scheduledEnd: stringOrNull(core.do_date_end ?? task.scheduledEnd ?? task.scheduled_end),
        dueDate: stringOrNull(core.due_date ?? task.dueDate ?? task.due_date),
        createdAt: stringOrNull(task.created_at ?? task.createdAt),
        updatedAt: stringOrNull(task.updated_at ?? task.updatedAt),
        requiredP: numberOrNull(state.required_productivity_energy),
        requiredRJ: numberOrNull(state.required_rejuvenation_energy),
        returnP: numberOrNull(state.productivity_return),
        returnRJ: numberOrNull(state.rejuvenation_return),
        resistance: numberOrNull(state.resistance),
        focus: numberOrNull(custom.actual_session?.focus_level ?? ai.focus_level),
        emotion: String(custom.emotion_word || custom.emotion || custom.emotion_label || ai.predicted_emotion || 'Unknown'),
        customAttributes: custom,
      };
    })
    .filter(activity => activity.id);
}

function filterByRange(activities: ActivitySample[], range: TimeRange): ActivitySample[] {
  if (range === 'all') return activities;
  const now = new Date();
  const start = new Date(now);
  const days = range === 'week' ? 7 : range === 'month' ? 31 : range === 'quarter' ? 92 : 366;
  start.setDate(now.getDate() - days);
  return activities.filter(activity => {
    const value = activity.scheduledStart || activity.updatedAt || activity.createdAt || activity.dueDate;
    if (!value) return true;
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return true;
    return date >= start && date <= now;
  });
}

function buildNumericSeries(activities: ActivitySample[], exportedTraining: Record<string, any> | null): NumericSeries[] {
  const trainingRows = Array.isArray(exportedTraining?.durationTrainingData)
    ? exportedTraining.durationTrainingData
    : [];
  const actualDurationsByTask = new Map<string, number>();
  for (const row of trainingRows) {
    const taskId = String(row?.task_id || '');
    const actual = numberOrNull(row?.actual_duration);
    if (taskId && actual !== null) actualDurationsByTask.set(taskId, actual);
  }

  const base: NumericSeries[] = [
    { key: 'duration', label: 'Duration', values: activities.map(activity => activity.durationMinutes) },
    { key: 'actual_duration', label: 'Actual duration', values: activities.map(activity => actualDurationsByTask.get(activity.id) ?? null) },
    { key: 'remaining', label: 'Remaining', values: activities.map(activity => activity.remainingMinutes) },
    { key: 'completion', label: 'Completion %', values: activities.map(activity => activity.completionPercent) },
    { key: 'priority', label: 'Priority', values: activities.map(activity => activity.priorityScore) },
    { key: 'required_p', label: 'Required P', values: activities.map(activity => activity.requiredP) },
    { key: 'required_rj', label: 'Required RJ', values: activities.map(activity => activity.requiredRJ) },
    { key: 'p_return', label: 'P return', values: activities.map(activity => activity.returnP) },
    { key: 'rj_return', label: 'RJ return', values: activities.map(activity => activity.returnRJ) },
    { key: 'net_p', label: 'Net P', values: activities.map(activity => difference(activity.returnP, activity.requiredP)) },
    { key: 'net_rj', label: 'Net RJ', values: activities.map(activity => difference(activity.returnRJ, activity.requiredRJ)) },
    { key: 'resistance', label: 'Resistance', values: activities.map(activity => activity.resistance) },
    { key: 'focus', label: 'Focus', values: activities.map(activity => activity.focus) },
    { key: 'hour', label: 'Hour', values: activities.map(activity => hourOf(activity.scheduledStart)) },
    { key: 'weekday', label: 'Weekday', values: activities.map(activity => weekdayOf(activity.scheduledStart)) },
  ];

  const customKeys = new Set<string>();
  for (const activity of activities) {
    for (const [key, value] of Object.entries(activity.customAttributes)) {
      if (numberOrNull(value) !== null) customKeys.add(key);
    }
  }
  for (const key of customKeys) {
    base.push({
      key: `custom:${key}`,
      label: prettifyKey(key),
      values: activities.map(activity => numberOrNull(activity.customAttributes[key])),
    });
  }
  return base.filter(item => item.values.some(value => value !== null));
}

function buildCorrelations(series: NumericSeries[]) {
  const rows: Array<{ a: string; b: string; aLabel: string; bLabel: string; r: number }> = [];
  for (let a = 0; a < series.length; a += 1) {
    for (let b = a + 1; b < series.length; b += 1) {
      const left = series[a]!;
      const right = series[b]!;
      const r = pearson(left.values, right.values);
      if (r === null) continue;
      rows.push({ a: left.key, b: right.key, aLabel: left.label, bLabel: right.label, r });
    }
  }
  return rows.sort((left, right) => Math.abs(right.r) - Math.abs(left.r));
}

function runAnalysisQuery(query: string, series: NumericSeries[], activities: ActivitySample[]): string {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return 'Enter a command.';
  if (series.length === 0) return 'No numeric metric series in this range yet.';
  if (normalized.startsWith('correlate')) {
    const [, aKey, bKey] = normalized.split(/\s+/);
    const a = findSeries(series, aKey);
    const b = findSeries(series, bKey);
    if (!a || !b) return `Unknown metric. Available: ${series.map(item => item.key).join(', ')}`;
    const r = pearson(a.values, b.values);
    return r === null
      ? `Not enough paired numeric samples for ${a.label} and ${b.label}.`
      : `corr(${a.label}, ${b.label}) = ${formatNumber(r, 3)} across ${pairedCount(a.values, b.values)} paired samples.`;
  }
  if (normalized.startsWith('average')) {
    const match = normalized.match(/^average\s+(.+?)\s+by\s+(.+)$/);
    if (!match) return 'Use: average duration by bundle';
    const metric = findSeries(series, match[1]?.trim());
    const group = match[2]?.trim() || '';
    if (!metric) return `Unknown metric. Available: ${series.map(item => item.key).join(', ')}`;
    const groups = new Map<string, number[]>();
    activities.forEach((activity, index) => {
      const value = metric.values[index];
      if (value === null || !Number.isFinite(value)) return;
      const key =
        group.includes('emotion') ? activity.emotion :
        group.includes('project') ? activity.project :
        group.includes('assignee') ? activity.assignee :
        activity.bundle;
      groups.set(key, [...(groups.get(key) || []), value]);
    });
    if (groups.size === 0) return 'No grouped numeric samples.';
    return [...groups.entries()]
      .map(([key, values]) => `${key}: ${formatNumber(average(values), 2)} (n=${values.length})`)
      .join('\n');
  }
  return 'Supported commands: correlate <metric> <metric>; average <metric> by bundle|project|emotion|assignee.';
}

function zipSeries(xSeries?: NumericSeries, ySeries?: NumericSeries): Array<{ x: number; y: number }> {
  if (!xSeries || !ySeries) return [];
  const length = Math.min(xSeries.values.length, ySeries.values.length);
  const points: Array<{ x: number; y: number }> = [];
  for (let index = 0; index < length; index += 1) {
    const x = xSeries.values[index];
    const y = ySeries.values[index];
    if (x === null || y === null || !Number.isFinite(x) || !Number.isFinite(y)) continue;
    points.push({ x, y });
  }
  return points;
}

function groupByRoundedX(points: Array<{ x: number; y: number }>) {
  const groups = new Map<number, number[]>();
  for (const point of points) {
    const key = Math.round(point.x);
    groups.set(key, [...(groups.get(key) || []), point.y]);
  }
  return [...groups.entries()]
    .map(([x, values]) => ({ x, y: average(values), count: values.length }))
    .sort((left, right) => left.x - right.x);
}

function findSeries(series: NumericSeries[], key?: string) {
  const normalized = String(key || '').replace(/\s+/g, '_');
  return series.find(item => item.key === normalized || item.key.endsWith(`:${normalized}`) || item.label.toLowerCase().replace(/\s+/g, '_') === normalized);
}

function pearson(aValues: Array<number | null>, bValues: Array<number | null>): number | null {
  const pairs: Array<[number, number]> = [];
  const length = Math.min(aValues.length, bValues.length);
  for (let index = 0; index < length; index += 1) {
    const a = aValues[index];
    const b = bValues[index];
    if (a === null || b === null || !Number.isFinite(a) || !Number.isFinite(b)) continue;
    pairs.push([a, b]);
  }
  if (pairs.length < 3) return null;
  const avgA = average(pairs.map(pair => pair[0]));
  const avgB = average(pairs.map(pair => pair[1]));
  const numerator = pairs.reduce((sum, [a, b]) => sum + (a - avgA) * (b - avgB), 0);
  const denomA = Math.sqrt(pairs.reduce((sum, [a]) => sum + (a - avgA) ** 2, 0));
  const denomB = Math.sqrt(pairs.reduce((sum, [, b]) => sum + (b - avgB) ** 2, 0));
  const denominator = denomA * denomB;
  if (!denominator) return null;
  return numerator / denominator;
}

function pairedCount(aValues: Array<number | null>, bValues: Array<number | null>): number {
  return zipSeries({ key: 'a', label: 'a', values: aValues }, { key: 'b', label: 'b', values: bValues }).length;
}

function average(values: Array<number | null>): number {
  const finite = values.filter((value): value is number => value !== null && Number.isFinite(value));
  if (finite.length === 0) return 0;
  return finite.reduce((sum, value) => sum + value, 0) / finite.length;
}

function percent(part: number, total: number): number {
  return total > 0 ? (part / total) * 100 : 0;
}

function formatNumber(value: unknown, digits = 1): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return digits === 0 ? '0' : Number(0).toFixed(digits);
  return numeric.toFixed(digits);
}

function safeRecord(value: unknown): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {};
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function stringOrNull(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
}

function isCompleted(status: string): boolean {
  return ['completed', 'done'].includes(status.toLowerCase());
}

function priorityToNumber(value: unknown): number | null {
  const numeric = numberOrNull(value);
  if (numeric !== null) return numeric;
  const text = String(value || '').toLowerCase();
  if (text === 'urgent') return 10;
  if (text === 'high') return 7;
  if (text === 'medium') return 5;
  if (text === 'low') return 2;
  return null;
}

function difference(a: number | null, b: number | null): number | null {
  if (a === null || b === null) return null;
  return a - b;
}

function hourOf(value: string | null): number | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.getHours() : null;
}

function weekdayOf(value: string | null): number | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.getDay() : null;
}

function prettifyKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
}

function downloadJson(data: Record<string, any>) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `fluxiplanner-training-data-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  URL.revokeObjectURL(url);
}
